import { isFilled } from "@prismicio/client";
import { PrismicRichText } from "@prismicio/react";
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next";
import { Icon } from "@/components";
import SectionHeader from "./SectionHeader";
import SectionCta from "./SectionCta";
import Styles from "./Numbered.module.css";

/**
 * TextGrid — `numbered` variation. Source: `.services-grid` / `.service-card`.
 *
 * Each card leads with one of three optional media, in priority order:
 *   image  -> full-bleed card-top photo
 *   icon   -> accent-tinted square badge (registry Select; the "—" sentinel and
 *             an empty value both count as unset)
 *   number -> the authored number, as originally shipped
 * When none is set, nothing renders in that slot.
 *
 * Grid flow is count-aware (see the CSS): counts divisible by three cap at
 * three across so six cards flow 3x2 rather than auto-fit's 4+2 orphan row.
 *
 * Each card takes an optional CTA link (FeatureCards' `.cardCta` pattern),
 * gated on both label and link. The card itself is not a link, so a CTA and
 * any links in the description can coexist without nesting anchors.
 */
const Numbered = ({ primary, items }) => {
	return (
		<section
			className={Styles.wrapper}
			data-theme={primary?.dark_theme ? "dark" : "light"}>
			<div
				className={Styles.container}
				data-contain="true">
				<SectionHeader
					eyebrow={primary?.eyebrow}
					heading={primary?.heading}
					intro={primary?.intro}
				/>
				<ul
					className={Styles.grid}
					data-count={items?.length}>
					{items?.map((item, index) => {
						const hasImage = isFilled.image(item?.image);
						const hasIcon = item?.icon && item?.icon !== "—";
						const hasCta =
							item?.card_cta_label && isFilled.link(item?.card_cta_link);
						return (
							<li
								className={Styles.card}
								key={index}>
								{hasImage && (
									<div className={Styles.media}>
										<PrismicNextImage
											field={item?.image}
											fill
											sizes="(max-width: 768px) 100vw, 33vw"
											style={{ objectFit: "cover" }}
										/>
									</div>
								)}
								<div className={Styles.cardBody}>
									{!hasImage && hasIcon ? (
										<span
											className={Styles.iconBadge}
											aria-hidden="true">
											<Icon
												icon={item?.icon}
												size={22}
											/>
										</span>
									) : !hasImage && item?.number ? (
										<span
											className={Styles.number}
											aria-hidden="true">
											{item?.number}
										</span>
									) : null}
									<h3 className={Styles.title}>{item?.title}</h3>
									{isFilled.richText(item?.description) && (
										<div className={Styles.description}>
											<PrismicRichText field={item?.description} />
										</div>
									)}
									{item?.tag && <span className={Styles.tag}>{item?.tag}</span>}
									{hasCta && (
										<PrismicNextLink
											field={item?.card_cta_link}
											className={Styles.cardCta}>
											{item?.card_cta_label}
										</PrismicNextLink>
									)}
								</div>
							</li>
						);
					})}
				</ul>
				<SectionCta
					label={primary?.cta_label}
					link={primary?.cta_link}
				/>
			</div>
		</section>
	);
};

export default Numbered;
